import type { CancelToken } from 'axios';
import axios from 'axios';
import type {
  QueryFunctionContext,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
} from 'react-query';
import { useInfiniteQuery } from 'react-query';

type Options<Return> = Omit<UseInfiniteQueryOptions, 'queryFn'> & {
  queryFn: (
    context: QueryFunctionContext<string | readonly unknown[], any>,
    cancelToken: CancelToken,
  ) => Promise<Return> | Return;
  debounceTime: number;
};

function useDebounceInfiniteQuery<Return = any>(options: Options<Return>) {
  return useInfiniteQuery({
    ...options,
    queryFn: (context) => {
      let cancelId: NodeJS.Timeout | undefined;
      const CancelToken = axios.CancelToken;
      const source = CancelToken.source();
      const promise = new Promise((resolve, reject) => {
        cancelId = setTimeout(() => {
          Promise.resolve(options.queryFn(context, source.token))
            .then(resolve)
            .catch(reject);
        }, options.debounceTime);
      });
      (promise as any).cancel = () => {
        clearTimeout(cancelId);
        source.cancel();
      };
      return promise;
    },
  }) as UseInfiniteQueryResult<Return>;
}

export default useDebounceInfiniteQuery;
